import {
  Badge,
  Box,
  Button,
  Card,
  Heading,
  IconInfo,
  IconSearch,
  IconTick,
  IconVisibility,
  Inline,
  List,
  Stack,
  Strong,
  Text,
  TooltipRenderer,
} from 'braid-design-system';

interface MobilePackageTilesProps {
  onFreemiumClick: () => void;
  onStandardClick: () => void;
  onAccelerateClick: () => void;
  htags: {
    freemium: string;
    standard: string;
    accelerate: string;
  };
}

export const MobilePackageTiles = (props: MobilePackageTilesProps) => (
  <Stack space="large">
    <Card data={{ testid: 'mobile-package-tile-freemium' }}>
      <Stack space="medium">
        <Stack space="small">
          <Heading level="3">Free</Heading>

          <Text tone="secondary">Starting your selling journey</Text>
        </Stack>

        <List type="icon" icon={<IconTick />}>
          <Text>Appear on Australia&apos;s #1 business marketplace</Text>
          <Text>Receive emails from interested buyers</Text>
          <Text>List for 6 months</Text>
        </List>

        <Button
          tone="formAccent"
          onClick={props.onFreemiumClick}
          data={{ htag: props.htags.freemium }}
        >
          Choose Free
        </Button>
      </Stack>
    </Card>

    <Card data={{ testid: 'mobile-package-tile-standard' }}>
      <Stack space="medium">
        <Stack space="small">
          <Heading level="3">Standard</Heading>

          <Text tone="secondary">Get noticed by more buyers</Text>
        </Stack>

        <Stack space="small" data={{ testid: 'package-price' }}>
          <Heading level="2">$949</Heading>

          <Text size="small">+ GST</Text>
        </Stack>

        <Stack space="small">
          <Text weight="strong">Everything in Free, plus:</Text>

          <List type="icon" icon={<IconTick />}>
            <Text>List until your business sells</Text>
            <Text>Photo included in search results</Text>
            <Text>Appear higher in search results</Text>
            <Text>Included in daily buyer recommendation emails</Text>
          </List>
        </Stack>

        <Stack space="small">
          <Inline space="xsmall" alignY="center">
            <Text weight="strong">Estimated performance</Text>
            <TooltipRenderer
              tooltip={<Text size="small">{standard.tooltipText}</Text>}
            >
              {({ triggerProps }) => (
                <Box {...triggerProps} display="flex" alignItems="center">
                  <IconInfo tone="formAccent" />
                </Box>
              )}
            </TooltipRenderer>
          </Inline>
          <Inline space="xsmall" alignY="center">
            <IconVisibility tone="formAccent" />
            <Text>
              <Strong>{standard.viewPerformanceStat}</Strong>{' '}
              {standard.viewPerformanceText}
            </Text>
          </Inline>
          <Inline space="xsmall" alignY="center">
            <IconSearch tone="formAccent" />
            <Text>
              <Strong>{standard.searchPerformanceStat}</Strong>{' '}
              {standard.searchPerformanceText}
            </Text>
          </Inline>
        </Stack>

        <Button
          tone="formAccent"
          onClick={props.onStandardClick}
          data={{ htag: props.htags.standard }}
        >
          Choose Standard
        </Button>
      </Stack>
    </Card>

    <Card data={{ testid: 'mobile-package-tile-accelerate' }}>
      <Stack space="medium">
        <Stack space="small">
          <Heading level="3">Accelerate</Heading>

          <Text tone="secondary">Reach the right buyers faster</Text>
        </Stack>

        <Badge tone="promote">Best performing</Badge>

        <Stack space="small" data={{ testid: 'package-price' }}>
          <Heading level="2">$1299</Heading>
          <Text size="small">+ GST</Text>
        </Stack>

        <Stack space="small">
          <Text weight="strong">Everything in Standard, plus:</Text>

          <List type="icon" icon={<IconTick />}>
            <Text>Feature at the top of search results</Text>
            <Text>Priority placement in daily buyer recommendation emails</Text>
          </List>
        </Stack>

        <Stack space="small">
          <Inline space="xsmall" alignY="center">
            <Text weight="strong">Estimated performance</Text>
            <TooltipRenderer
              tooltip={<Text size="small">{accelerate.tooltipText}</Text>}
            >
              {({ triggerProps }) => (
                <Box {...triggerProps} display="flex" alignItems="center">
                  <IconInfo tone="formAccent" />
                </Box>
              )}
            </TooltipRenderer>
          </Inline>

          <Inline space="xsmall" alignY="center">
            <IconVisibility tone="formAccent" />
            <Text>
              <Strong>{accelerate.viewPerformanceStat}</Strong>{' '}
              {accelerate.viewPerformanceText}
            </Text>
          </Inline>

          <Inline space="xsmall" alignY="center">
            <IconSearch tone="formAccent" />
            <Text>
              <Strong>{accelerate.searchPerformanceStat}</Strong>{' '}
              {accelerate.searchPerformanceText}
            </Text>
          </Inline>
        </Stack>

        <Button
          tone="formAccent"
          onClick={props.onAccelerateClick}
          data={{ htag: props.htags.accelerate }}
        >
          Choose Accelerate
        </Button>
      </Stack>
    </Card>
  </Stack>
);

const standard = {
  viewPerformanceStat: '2.2x',
  viewPerformanceText: 'ad views',
  searchPerformanceStat: '4.4x',
  searchPerformanceText: 'search views',
  tooltipText:
    'Based on the average performance of Standard listings compared to Free listings during July 2025–June 2026.',
};

const accelerate = {
  viewPerformanceStat: '4.5x',
  viewPerformanceText: 'ad views',
  searchPerformanceStat: '6.8x',
  searchPerformanceText: 'search views',
  tooltipText:
    'Based on the average performance of Accelerate listings compared to Free listings during July 2025–June 2026.',
};
